import * as solidIcons from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { Typeahead } from 'react-bootstrap-typeahead';
import DatePicker from 'react-datepicker';
import { Control, Controller, FieldErrors, useFieldArray, UseFormRegister, UseFormWatch } from "react-hook-form";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { GetFormat, useGet } from "@zing/shared/api";
import { NUMBER_OF_SUBSCRIBE_OPTIONS } from "@zing/shared/constants";
import { TENANT_SHORT_NAME } from "@zing/shared/constants/zingConfigInfo";
import { trans } from "@zing/shared/i18n";
import { LabelInputFile } from "@zing/shared/styledComponents/fileComponents/styles";
import { Video } from "../components/video";
import { ProgramInputs } from "../schemas/inputs";
import { Niche } from "../schemas/types";
import { AddVideoButton, ProgramFormContainer, QuillEditorWrapper } from "../styled";

interface GeneralFormProps {
    register: UseFormRegister<ProgramInputs>,
    errors: FieldErrors<ProgramInputs>,
    control: Control<ProgramInputs>;
    watch: UseFormWatch<ProgramInputs>;
}

export const GeneralForm = ({ errors, register, control, watch }: GeneralFormProps) => {
    const [shortDescriptionLength, setShortDescriptionLength] = React.useState(0);
    const [longDescriptionLength, setLongDescriptionLength] = React.useState(0);

    const {
        append,
        remove,
        fields
    } = useFieldArray({ control, name: "videos" });

    const supportFilesArray = useFieldArray({
        control,
        name: "supportFiles"
    })

    const presentationVideoControl = useFieldArray({
        control,
        name: "presentationVideo"
    })

    const homePageSliderControl = useFieldArray({
        control,
        name: "homePageSlider"
    })

    const coverImageControl = useFieldArray({
        control,
        name: "coverImage"
    })

    useEffect(() => {
        if (fields.length === 0) {
            append({ name: "", url: "" });
        }
    }, [fields, append])

    // Inicializar contadores de caracteres com valores existentes
    useEffect(() => {
        const shortDesc = watch('shortDescription');
        const longDesc = watch('longDescription');

        if (shortDesc) {
            setShortDescriptionLength(shortDesc.length);
        }

        if (longDesc) {
            // Para ReactQuill, precisamos remover as tags HTML para contar apenas o texto
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = longDesc;
            const textContent = tempDiv.textContent || tempDiv.innerText || '';
            setLongDescriptionLength(textContent.length);
        }
    }, [watch('shortDescription'), watch('longDescription')])

    const nichesRequest: GetFormat = {
        url: 'v1/system/niche'
    }
    const { data: niches, isFetching } = useGet<any>(nichesRequest, 'niches');

    const handleChangeNiches = (selected: Niche[], onChange: any) => {
        onChange(selected);
    };

    const handleChangeDate = (value, onChange: any) => {
        onChange(value);
    };

    const addVideo = useCallback(() => {
        if (fields.length >= 3) return;
        append({ name: "", url: "" });
    }, [fields.length, append]);

    const removeVideo = (index: number) => {
        remove(index);
    };

    const addSupportFile = (file: File) => {
        supportFilesArray.append(file);
    }
    const removeFromSupportFile = (index: number) => {
        supportFilesArray.remove(index);
    }

    const addPresentationVideo = (file: File) => {
        presentationVideoControl.append(file);
    }
    const removeFromPresentationVideo = (index: number) => {
        presentationVideoControl.remove(index);
    }

    const addHomePageSlider = (file: File) => {
        homePageSliderControl.append(file);
    }
    const removeFromHomePageSlider = (index: number) => {
        homePageSliderControl.remove(index);
    }

    const addCoverImage = (file: File) => {
        coverImageControl.append(file);
    }
    const removeFromCoverImage = (index: number) => {
        coverImageControl.remove(index);
    }

    const getFileIcon = (fileName: string | null) => {
        if (!fileName) return solidIcons.faFile;

        const ext = fileName.split('.').pop()?.toLowerCase();
        if (!ext) return solidIcons.faFile;

        const imageExts = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "tiff"];

        if (imageExts.includes(ext)) {
            return solidIcons.faFileImage;
        }

        const iconName = `faFile${ext.charAt(0).toUpperCase()}${ext.slice(1)}`;

        return (solidIcons as any)[iconName] ?? solidIcons.faFile;
    };


    const isActive = watch('isActive');

    return (
        <ProgramFormContainer>
            <Row>
                <Col>
                    <Form.Group controlId="title">
                        <Form.Label required>Nome do programa</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Nome do Programa"
                            {...register("title")}
                            isInvalid={!!errors.title}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.title?.message}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form.Group controlId="niches">
                        <Form.Label required>Segmento/Setor</Form.Label>
                        <Controller
                            control={control}
                            name="niches"
                            render={({ field }) => (
                                <Typeahead
                                    id="niches"
                                    labelKey="name"
                                    caseSensitive={false}
                                    emptyLabel="Não existem segmento/setor cadastrados"
                                    options={niches?.rows ?? []}
                                    selected={field.value || []}
                                    onChange={(selected) => handleChangeNiches(selected as Niche[], field.onChange)}
                                    className={errors.niches ? "form-control is-invalid" : "form-control "}
                                />
                            )}
                        />
                        {errors.niches && (
                            <Form.Control.Feedback type="invalid" className="d-block">
                                {errors.niches?.message}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form.Group controlId="shortDescription">
                        <Form.Label required>Descrição curta</Form.Label>
                        <Controller
                            control={control}
                            name="shortDescription"
                            render={({ field }) => (
                                <>
                                    <Form.Control
                                        type="text"
                                        placeholder="Digite uma descrição curta"
                                        maxLength={280}
                                        value={field.value || ''}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setShortDescriptionLength(e.target.value.length);
                                        }}
                                        isInvalid={!!errors.shortDescription}
                                    />
                                    <Form.Text className="text-muted d-block text-end" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                                        {shortDescriptionLength}/280 caracteres
                                    </Form.Text>
                                </>
                            )}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.shortDescription?.message}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form.Group controlId="longDescription">
                        <Form.Label required>Descrição longa</Form.Label>
                        <Controller
                            control={control}
                            name="longDescription"
                            render={({ field }) => (
                                <>
                                    <QuillEditorWrapper>
                                        <ReactQuill
                                            theme="snow"
                                            value={field.value || ''}
                                            onChange={(content, delta, source, editor) => {
                                                field.onChange(content);
                                                const text = editor.getText();
                                                setLongDescriptionLength(text.length - 1); // Remove o caractere de nova linha do final
                                            }}
                                            modules={{
                                                toolbar: [
                                                    [{ 'header': [1, 2, 3, false] }],
                                                    ['bold', 'italic', 'underline', 'strike'],
                                                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                                    [{ 'align': [] }],
                                                    ['link'],
                                                    ['clean']
                                                ]
                                            }}
                                            placeholder="Digite uma descrição longa"
                                        />
                                    </QuillEditorWrapper>
                                    <Form.Text className="text-muted d-block text-end" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                                        {longDescriptionLength}/5000 caracteres
                                    </Form.Text>
                                </>
                            )}
                        />
                        {errors.longDescription && (
                            <Form.Control.Feedback type="invalid" className="d-block">
                                {errors.longDescription?.message}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                </Col>
            </Row>
            <Row className="program-date-row">
                <Col>
                    <Form.Group controlId="lauchDate">
                        <Form.Label required>Data de início das inscrições</Form.Label>
                        <Controller
                            control={control}
                            name="lauchDate"
                            rules={{ required: "Informe a data de início das inscrições" }}
                            render={({ field }) => (
                                <DatePicker
                                    id="lauchDate"
                                    className={errors.lauchDate ? 'form-control is-invalid' : 'form-control'}
                                    dateFormat='dd/MM/yyyy'
                                    minDate={new Date()}
                                    maxDate={null}
                                    selected={field.value || null}
                                    onChange={(value) => handleChangeDate(value, field.onChange)}
                                />
                            )}
                        />
                        {errors.lauchDate && (
                            <Form.Control.Feedback type="invalid" className="d-block">
                                {errors.lauchDate?.message}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                </Col>
                <Col>
                    <Form.Group controlId="finalSelectionDate">
                        <Form.Label required>Data de encerramento das inscrições</Form.Label>
                        <Controller
                            control={control}
                            name="finalSelectionDate"
                            rules={{ required: "Informe a data de encerramento das inscrições" }}
                            render={({ field }) => (
                                <DatePicker
                                    id="finalSelectionDate"
                                    className={errors.finalSelectionDate ? 'form-control is-invalid' : 'form-control'}
                                    dateFormat='dd/MM/yyyy'
                                    minDate={null}
                                    maxDate={null}
                                    selected={field.value || null}
                                    onChange={(value) => handleChangeDate(value, field.onChange)}
                                />
                            )}
                        />
                        {errors.finalSelectionDate && (
                            <Form.Control.Feedback type="invalid" className="d-block">
                                {errors.finalSelectionDate?.message}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form.Group controlId="coverImage">
                        <Form.Label required className="w-full mb-0">Imagem de divulgação pequena</Form.Label>
                        <p className="text-small w-full">Dimensão aceita 256x104 px</p>
                        <Controller
                            control={control}
                            name="coverImage"
                            rules={{ required: "Selecione uma imagem para divulgação pequena" }}
                            render={({ field }) => (
                                <div className="d-flex flex-column gap-2">
                                    {coverImageControl.fields.map((item, key) => {
                                        const file = field.value?.[key];
                                        return (
                                            <div className="d-flex flex-row pl-4" key={key}>
                                                <div className="col-5 rounded-start d-flex align-items-center gap-2" style={{ backgroundColor: 'var(--secondary-p)', color: 'var(--primary)' }}>
                                                    <a href={file instanceof File ? URL.createObjectURL(file) : (file as any)?.stream} target="_blank" rel="noreferrer" className="text-decoration-none" style={{ color: 'var(--primary)' }}>
                                                        <FontAwesomeIcon icon={getFileIcon(file?.name ?? 'undefined')} className="ml-2 mr-2" />
                                                        {file?.name ?? 'undefined'}
                                                    </a>
                                                </div>
                                                <div className="d-flex align-items-center rounded-end" style={{ backgroundColor: 'var(--secondary-p)' }}>
                                                    <Button className="px-3 py-0 rounded-end bg-transparent" onClick={() => removeFromCoverImage(key)} style={{ backgroundColor: 'var(--secondary-p)', color: 'var(--primary)', border: 'none' }}>
                                                        <FontAwesomeIcon icon={['fas', 'close']} />
                                                    </Button>
                                                </div>
                                            </div>
                                        )
                                    })}
                                    {coverImageControl.fields.length < 1 &&
                                        <LabelInputFile isClickable className="button-add-file">
                                            <div className="row">
                                                <div className="col">
                                                    <FontAwesomeIcon icon={['fas', 'cloud-arrow-up']} className="mr-2" />
                                                    Adicionar Arquivo
                                                </div>
                                            </div>
                                            <div className="row d-none">
                                                <div className="col">
                                                    <input
                                                        type="file"
                                                        name={field.name}
                                                        ref={field.ref}
                                                        accept="image/*"
                                                        onBlur={field.onBlur}
                                                        className="form-control"
                                                        id="coverImage"
                                                        onChange={(event) => {
                                                            const element = event?.target as HTMLInputElement
                                                                ;[].slice.call(element.files).forEach((file: File) => addCoverImage(file));
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </LabelInputFile>
                                    }
                                </div>
                            )}
                        />
                        {errors.coverImage && (
                            <Form.Control.Feedback type="invalid" className="d-block">
                                {errors.coverImage?.message}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form.Group controlId="homePageSlider">
                        <Form.Label required className="mb-0">Imagem de divulgação grande</Form.Label>
                        <p className="text-small w-full">Dimensão aceita 1120x240 px</p>
                        <Controller
                            control={control}
                            name="homePageSlider"
                            rules={{ required: "Selecione uma imagem para divulgação grande" }}
                            render={({ field }) => (
                                <div className="d-flex flex-column gap-2">
                                    {homePageSliderControl.fields.map((item, key) => {
                                        const file = field.value?.[key];
                                        return (
                                            <div className="d-flex flex-row pl-4" key={item.id}>
                                                <div className="col-5 rounded-start d-flex align-items-center gap-2" style={{ backgroundColor: 'var(--secondary-p)', color: 'var(--primary)' }}>
                                                    <a href={file instanceof File ? URL.createObjectURL(file) : (file as any)?.stream} target="_blank" rel="noreferrer" className="text-decoration-none" style={{ color: 'var(--primary)' }}>
                                                        <FontAwesomeIcon icon={getFileIcon(file?.name ?? 'undefined')} className="ml-2 mr-2" />
                                                        {file?.name ?? 'undefined'}
                                                    </a>
                                                </div>
                                                <div className="d-flex align-items-center rounded-end" style={{ backgroundColor: 'var(--secondary-p)' }}>
                                                    <Button className="px-3 py-0 rounded-end bg-transparent" onClick={() => removeFromHomePageSlider(key)} style={{ backgroundColor: 'var(--secondary-p)', color: 'var(--primary)', border: 'none' }}>
                                                        <FontAwesomeIcon icon={['fas', 'close']} />
                                                    </Button>
                                                </div>
                                            </div>
                                        )
                                    })}
                                    {homePageSliderControl.fields.length < 1 &&
                                        <LabelInputFile isClickable className="button-add-file">
                                            <div className="row">
                                                <div className="col">
                                                    <FontAwesomeIcon icon={['fas', 'cloud-arrow-up']} className="mr-2" />
                                                    Adicionar Arquivo
                                                </div>
                                            </div>
                                            <div className="row d-none">
                                                <div className="col">
                                                    <input
                                                        type="file"
                                                        name={field.name}
                                                        ref={field.ref}
                                                        accept="image/*"
                                                        onBlur={field.onBlur}
                                                        className="form-control"
                                                        id="homePageSlider"
                                                        onChange={(event) => {
                                                            const element = event?.target as HTMLInputElement
                                                                ;[].slice.call(element.files).forEach((file: File) => addHomePageSlider(file));
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </LabelInputFile>}
                                </div>
                            )}
                        />
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form.Group controlId="presentationVideo">
                        <Form.Label>Vídeo de divulgação</Form.Label>
                        <Controller
                            control={control}
                            name="presentationVideo"
                            rules={{ required: "Selecione um vídeo para divulgação" }}
                            render={({ field }) => (
                                <div className="d-flex flex-column gap-2">
                                    {presentationVideoControl.fields.map((item, key) => {
                                        const file = field.value?.[key];
                                        return (
                                            <div className="d-flex flex-row pl-4" key={item.id}>
                                                <div className="col-5 rounded-start d-flex align-items-center gap-2" style={{ backgroundColor: 'var(--secondary-p)', color: 'var(--primary)' }}>
                                                    <a href={file instanceof File ? URL.createObjectURL(file) : (file as any)?.stream} target="_blank" rel="noreferrer" className="text-decoration-none" style={{ color: 'var(--primary)' }}>
                                                        <FontAwesomeIcon icon={getFileIcon(file?.name ?? 'undefined')} className="ml-2 mr-2" />
                                                        {file?.name ?? 'undefined'}
                                                    </a>
                                                </div>
                                                <div className="d-flex align-items-center rounded-end" style={{ backgroundColor: 'var(--secondary-p)' }}>
                                                    <Button className="px-3 py-0 rounded-end bg-transparent" onClick={() => removeFromPresentationVideo(key)} style={{ backgroundColor: 'var(--secondary-p)', color: 'var(--primary)', border: 'none' }}>
                                                        <FontAwesomeIcon icon={['fas', 'close']} />
                                                    </Button>
                                                </div>
                                            </div>
                                        )
                                    })}
                                    {presentationVideoControl.fields.length < 1 &&
                                        <LabelInputFile isClickable className="button-add-file">
                                            <div className="row">
                                                <div className="col">
                                                    <FontAwesomeIcon icon={['fas', 'cloud-arrow-up']} className="mr-2" />
                                                    Adicionar Arquivo
                                                </div>
                                            </div>
                                            <div className="row d-none">
                                                <div className="col">
                                                    <input
                                                        type="file"
                                                        name={field.name}
                                                        ref={field.ref}
                                                        accept="video/*"
                                                        onBlur={field.onBlur}
                                                        className="form-control"
                                                        id="presentationVideo"
                                                        onChange={(event) => {
                                                            const element = event?.target as HTMLInputElement
                                                                ;[].slice.call(element.files).forEach((file: File) => addPresentationVideo(file));
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </LabelInputFile>}
                                </div>
                            )}
                        />
                    </Form.Group>
                </Col>
            </Row>
            <>
                {fields.map((field, index) => (
                    <Video
                        key={field.id}
                        index={index}
                        register={register}
                        errors={errors}
                        removeVideo={removeVideo}
                    />
                ))}
                <Row>
                    <Col>
                        <AddVideoButton disabled={fields?.length >= 3} onClick={addVideo}>
                            <FontAwesomeIcon icon={['fas', 'plus']} />
                            Adicionar outro vídeo
                        </AddVideoButton>
                    </Col>
                </Row>
            </>
            <Row>
                <Col>
                    <Form.Group controlId="supportFiles">
                        <Form.Label>Documentos de apoio</Form.Label>
                        <Controller
                            control={control}
                            name="supportFiles"
                            rules={{ required: "Selecione uma imagem para divulgação pequena" }}
                            render={({ field }) => (
                                <div className="d-flex flex-column gap-2">
                                    {supportFilesArray.fields.map((item, key) => {
                                        const file = field.value?.[key];
                                        return (
                                            <div className="d-flex flex-row pl-4" key={item.id}>
                                                <div className="col-5 rounded-start d-flex align-items-center gap-2" style={{ backgroundColor: 'var(--secondary-p)', color: 'var(--primary)' }}>
                                                    <a href={file instanceof File ? URL.createObjectURL(file) : (file as any)?.stream} target="_blank" rel="noreferrer" className="text-decoration-none" style={{ color: 'var(--primary)' }}>
                                                        <FontAwesomeIcon icon={getFileIcon(file?.name ?? 'undefined')} className="ml-2 mr-2" />
                                                        {file?.name ?? 'undefined'}
                                                    </a>
                                                </div>
                                                <div className="d-flex align-items-center rounded-end" style={{ backgroundColor: 'var(--secondary-p)' }}>
                                                    <Button className="px-3 py-0 rounded-end bg-transparent" onClick={() => removeFromSupportFile(key)} style={{ backgroundColor: 'var(--secondary-p)', color: 'var(--primary)', border: 'none' }}>
                                                        <FontAwesomeIcon icon={['fas', 'close']} />
                                                    </Button>
                                                </div>
                                            </div>
                                        )
                                    })}
                                    <LabelInputFile isClickable className="button-add-file">
                                        <div className="row">
                                            <div className="col">
                                                <FontAwesomeIcon icon={['fas', 'cloud-arrow-up']} className="mr-2" />
                                                Adicionar Arquivo
                                            </div>
                                        </div>
                                        <div className="row d-none">
                                            <div className="col">
                                                <input
                                                    type="file"
                                                    name={field.name}
                                                    ref={field.ref}
                                                    onBlur={field.onBlur}
                                                    multiple
                                                    className="form-control"
                                                    id="supportFiles"
                                                    onChange={(event) => {
                                                        const element = event?.target as HTMLInputElement
                                                            ;[].slice.call(element.files).forEach((file: File) => addSupportFile(file));
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </LabelInputFile>
                                </div>
                            )}
                        />
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form.Group controlId="limitOfIdeas">
                        <Form.Label required>Quantas vezes um solucionador poderá se inscrever no programa?</Form.Label>
                        <Form.Select
                            className="form-control"
                            isInvalid={!!errors.limitOfIdeas}
                            isValid={!!errors.limitOfIdeas}
                            {...register('limitOfIdeas')}
                        >
                            {
                                NUMBER_OF_SUBSCRIBE_OPTIONS.map((item) => {
                                    return (
                                        <option key={item.id} value={item.value}>{item.label}</option>
                                    );
                                })
                            }
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {errors.limitOfIdeas?.message}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form.Group controlId="isActive">
                        <Form.Label required>Visibilidade do programa</Form.Label>
                        <Form.Check
                            type="radio"
                            id="idActive-public"
                            {...register("isActive")}
                            value="public"
                            label={
                                <>
                                    <FontAwesomeIcon
                                        className="radio-icon"
                                        icon={isActive === "public" ? ['far', 'circle-dot'] : ['far', 'circle']}
                                        style={{color: 'var(--primary)' }}
                                    />
                                    {trans('Público, o programa será exibido na vitrine do site de {{tenantShortName}}, acessível a todos.', { tenantShortName: TENANT_SHORT_NAME })}
                                </>
                            }
                        />
                        <Form.Check
                            type="radio"
                            id="idActive-private"
                            value="private"
                            {...register("isActive")}
                            label={
                                <>
                                    <FontAwesomeIcon
                                        className="radio-icon"
                                        icon={isActive === "private" ? ['far', 'circle-dot'] : ['far', 'circle']}
                                        style={{color: 'var(--primary)' }}
                                    />
                                    Privado, somente quem tiver logado poderá visualizar o programa.
                                </>
                            }
                        />
                        {errors.isActive && (
                            <Form.Control.Feedback type="invalid" className="d-block">
                                {errors.isActive?.message}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                </Col>
            </Row>
        </ProgramFormContainer >
    );
};